/**
 * usePrest0nSession Hook
 *
 * Manages the Prest0n session lifecycle for migrated accounts.
 * - Auto-creates or resumes a session on mount
 * - Persists sessionId in localStorage for tab-refresh recovery
 * - Provides sendTurn() for sending messages with streaming callbacks
 * - Exposes session state (isLoading, isStreaming, error)
 * - Tracks CONCURRENT turns in a per-turn ledger keyed by taskId/turn key
 *   (2026-08-16 concurrent-turn state stomp fix): each turn carries its own
 *   poll state and origin-thread binding; isStreaming/isPollingTask/
 *   activeTask are a derived view for legacy consumers, and
 *   getTurnForThread/getTurnForTask expose the precise per-turn state
 *
 * Usage:
 *   const { session, sendTurn, isStreaming, isLoading, error } =
 *     usePrest0nSession(accountId, pageContext);
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import {
  getPrest0nTaskStatus,
  getPrest0nTaskStatusDetailed,
  resumeOrCreatePrest0nSession,
  sendPrest0nTurn,
  triggerPrest0nTask,
  updatePrest0nSessionContext,
  toTurnAttachments,
} from './client';
import type { Prest0nTaskRecord, SessionInfo, Prest0nSessionState, TurnStreamCallbacks, TurnAttachment } from './types';
import type { OrchestratorPageContext } from '../../config/pageContextRegistry';
import type { ChatMessage, SerializedAttachment } from '../../components/GlobalChatSystem/types';
import { toSerializedAttachment, type RawAttachmentRecord } from '../../components/GlobalChatSystem/attachmentRecords';
import { extractTaskTokenUsages, taskMintTimestampMs } from '../../components/GlobalChatSystem/serverTaskResume';
import { isLiveSendInFlight } from '../../components/GlobalChatSystem/liveSendRegistry';
import { fernandoTrace, isFernandoStrictMode, FERN_FE_CODES } from '../prest0nRuntime/fernandoTrace';
import { getThreadMessages, upsertThreadMessages } from '../../repositories/chatThreadMessagesRepository';
import {
  clearQueuedTask,
  loadQueuedTask,
  mergeRecoveredAnswerIntoMessages,
  persistQueuedTask,
  resolveClientTaskAnchorId,
  resumeQueuedTaskOnHydrate,
  type PersistedQueuedTask,
} from './taskRecovery';
import { getOrMintStandaloneConversationId } from './conversationIdentity';
import { refusalFromError, type TurnRefusal } from './turnRefusal';
import { publishUsageWarning } from './usageWarningChannel';
import { logOrchestratorTurnError } from '../globalTools/orchestratorDecisionLog';

// ============================================================================
// Stall watchdog (async-turn hang fix, 2026-07-31)
// ============================================================================
// A dedicated-VM task can be acked (202) and then never progress: status stays
// pending/running while the task doc's updatedAt sits frozen, and the server
// sweeper only terminalizes it after 12-25+ minutes. The live poll tracks
// updatedAt across ticks. Calibration: during VM dispatch the server bumps
// updatedAt every 30s (taskQueue VM_LIVENESS_STAMP_INTERVAL_MS) — 3 minutes
// unchanged = 6 missed heartbeats, i.e. genuinely stalled, not merely quiet.
// Resume hops and stage keep-alives also bump updatedAt, which legitimately
// resets the timer. The server deliberately writes NO currentStage pulse (it
// would inject non-localized user-visible copy), so the stage-less
// onTaskStatus repaint in useGlobalTools is the pane clock's sole refresher.

/** Unchanged updatedAt for this long (6 missed 30s liveness heartbeats) → surface the honest holding label. */
export const TASK_STALL_HOLDING_LABEL_MS = 3 * 60 * 1000;
/**
 * Unchanged updatedAt for this long → stop polling and surface the retryable
 * error card instead of an indefinitely frozen pill.
 */
export const TASK_STALL_ERROR_MS = 6 * 60 * 1000;

/**
 * Honest holding copy — shared by the poll-exhaustion detach (rank #5) and
 * the 3-minute stall holding state.
 */
export const TASK_STILL_WORKING_HOLDING_LABEL =
  'still working — will update automatically when finished';

/**
 * Escalated holding copy at the (former) 6-minute abort threshold — the
 * client no longer declares death while the server sweeper owns the terminal
 * verdict (2026-08-11 Fernando DR incident: the premature Interrupted card
 * made the user regenerate on top of a live run and double-spend it).
 */
export const TASK_STALL_ESCALATED_HOLDING_LABEL =
  'taking longer than usual — still working; this will update automatically';

/** Structured error the 6-minute stall abort hands to onError consumers. */
export function buildTaskStallErrorInfo(taskId: string): {
  code: string;
  traceId: string;
  userMessage: string;
  userMessageEs: string;
} {
  return {
    code: 'CLIENT_TASK_STALL_TIMEOUT',
    // The task id doubles as the support-lookup ref ("(ref: task_…)").
    traceId: taskId,
    userMessage:
      "This is taking much longer than it should, so I've stopped waiting. Please try again.",
    userMessageEs:
      'Esto está tardando mucho más de lo debido, así que dejé de esperar. Por favor, inténtalo de nuevo.',
  };
}

// ============================================================================
// Per-turn live-turn ledger (concurrent-turn state stomp fix, 2026-08-16)
// ============================================================================
// Owner repro (post-#855, Fernando): thread A had a deep-research turn
// polling when a send in thread B replaced the hook's SINGLE
// {isStreaming, isPollingTask, activeTask} state — the sendTurn entry stomp
// cleared A's poll indication, and whichever poll ticked last owned
// activeTask, so every consumer that read the shared fields at delivery time
// (resume skip-guards, wedge checks, turn indicators) bound to "whichever
// turn wrote last" instead of its own. The delivery misbind + the orphaned
// DR turn were the visible symptoms.
//
// Fix: concurrent turns are tracked in a ledger keyed by a per-turn key
// (the pre-flight clientTaskId when minted, else a local turn key), each
// entry carrying its ORIGIN THREAD captured at TURN START, its own phase,
// and its own latest task record. A new send only ever writes ITS OWN entry;
// the legacy isStreaming/isPollingTask/activeTask surface is DERIVED (latest
// live turn wins, mirroring the old last-writer semantics for existing
// consumers), and per-thread/per-task accessors expose the precise state
// where thread-correctness matters (useGlobalChat resume guards + wedge
// checks). Delivery callbacks were already closed over per-turn at call time;
// the ledger makes the STATE follow the same discipline.

/** Phase of one tracked turn. 'terminal' entries linger briefly as a resume
 *  skip-guard (the old code kept the last terminal record in activeTask). */
export type Prest0nTurnPhase = 'streaming' | 'polling' | 'terminal';

export interface Prest0nLiveTurn {
  /** Stable ledger key for the turn's whole life (clientTaskId when the
   *  caller minted one, else a locally minted turn key). */
  turnKey: string;
  /** Server task id this turn is bound to — the pre-flight clientTaskId
   *  until onTaskQueued re-stamps it; null for inline SSE turns that never
   *  queue a task. NOT re-pointed by rescue-follows (the widget/registry
   *  know the ORIGINAL id; `task.taskId` carries the rescue id). */
  taskId: string | null;
  /** The thread this turn delivers to — captured at TURN START (same
   *  discipline PR #855 applied to resume delivery), never re-read from
   *  "current" state. Null only for legacy origin-less recovery records. */
  originThreadId: string | null;
  sessionId: string;
  phase: Prest0nTurnPhase;
  /** Latest observed task record (poll ticks / terminal doc). */
  task: Prest0nTaskRecord | null;
  startedAtMs: number;
  endedAtMs?: number;
  /** True for hydrate-recovery polls (initSession); live sends are false. */
  isRecovery: boolean;
}

/** Terminal entries kept as per-task resume skip-guards. Bounded so the
 *  ledger can never grow past a handful of finished turns; a new send on the
 *  same thread also evicts that thread's previous terminal entry. */
const MAX_TERMINAL_TURN_LEDGER = 8;

let localTurnKeySeq = 0;
/** Ledger key for turns whose caller minted no clientTaskId. */
function mintLocalTurnKey(): string {
  localTurnKeySeq += 1;
  return `turn_${Date.now().toString(36)}_${localTurnKeySeq.toString(36)}`;
}

// ============================================================================
// Hook
// ============================================================================

export interface UsePrest0nSessionReturn extends Prest0nSessionState {
  /**
   * Send a user turn to the backend session.
   * Streams events via the provided callbacks.
   * Returns false if no session exists (should trigger fallback).
   */
  sendTurn: (
    message: string,
    callbacks: TurnStreamCallbacks,
      options?: {
        attachments?: SerializedAttachment[];
        pageContext?: OrchestratorPageContext;
        signal?: AbortSignal;
        documentContent?: string;
        responseMode?: 'stream' | 'async' | 'auto';
        threadId?: string;
        /** Ingest-on-upload job id — see TurnRequest.ingestJobId. */
        ingestJobId?: string;
        requestedWorkflow?: 'deep-research' | 'routines';
        /** Pre-flight client-minted task id — see TurnRequest.clientTaskId. */
        clientTaskId?: string;
        /** Reply to a specific message (2026-09-15) — see TurnRequest.replyTo. */
        replyTo?: import('../../types/messageReply').MessageReplyRef | null;
      }
  ) => Promise<boolean>;

  /**
   * The typed REFUSAL from the most recent failed `sendTurn`, cleared on read.
   *
   * `sendTurn` reports failure as `false`, which cannot distinguish "the start
   * died, retry may work" from "the server declined, and said why". A refusal
   * must neither be retried (it will be declined identically) nor rendered as
   * the generic card (2026-09-12, the workspaceGate 'enforce' flip). Consumed
   * rather than read so a refusal can never leak onto a later, unrelated
   * failure; every other failure returns null and behaves exactly as before.
   */
  consumeTurnRefusal: () => TurnRefusal | null;

  /**
   * Force-refresh the session (e.g., after an auth change).
   */
  refreshSession: () => Promise<void>;

  /**
   * Per-turn ledger accessors (concurrent-turn state stomp fix, 2026-08-16).
   * Both read the synchronously-updated ledger ref — never lagging React
   * state — so effects and event handlers get the truth at call time.
   */
  /** The turn bound to a thread: live turn preferred, else the thread's most
   *  recent terminal entry. Null when the thread has no tracked turn. */
  getTurnForThread: (threadId: string | null | undefined) => Prest0nLiveTurn | null;
  /** The turn driving a task id (origin binding OR the latest rescue-followed
   *  record): live turn preferred, else the terminal skip-guard entry. */
  getTurnForTask: (taskId: string | null | undefined) => Prest0nLiveTurn | null;
  /** Live (non-terminal) turn count for a thread — the capacity input for
   *  the in-thread parallelism gate (slice 1, 2026-08-19). */
  countLiveTurnsForThread: (threadId: string | null | undefined) => number;
  /** Bumped on every ledger mutation — effect dependency for consumers that
   *  read the ledger through the accessors above. */
  liveTurnsVersion: number;
}

export function usePrest0nSession(
  accountId: string | null,
  pageContext?: OrchestratorPageContext,
  conversationId?: string,
  opts?: {
    /**
     * H1 (owner report 2026-07-14, spinner-outlives-turn): fired when a
     * RECOVERED queued task — the hydrate-resume poll started by initSession
     * with EMPTY callbacks — reaches a terminal status (completed or failed).
     * That poll finalizes the turn via the durable thread write
     * (persistCompletedTaskMessage) with no onDone/onError wired, so nothing
     * else releases the originating thread's processing flag; the host uses
     * this to dispatch the same thread-keyed stopProcessing the live turn's
     * completion path would have. `threadId` is the queued-task record's
     * originating thread (null when the legacy record lacked one). Live
     * sendTurn polls do NOT fire this — their own onDone/onError callbacks
     * own the release.
     */
    onRecoveredTaskTerminal?: (threadId: string | null, taskId: string) => void;
  }
): UsePrest0nSessionReturn {
  const [state, setState] = useState<Prest0nSessionState>({
    session: null,
    isStreaming: false,
    isLoading: false,
    isPollingTask: false,
    activeTask: null,
    error: null,
  });

  // Keep a ref to the current session so callbacks always have the latest value
  const sessionRef = useRef<SessionInfo | null>(null);
  // Track whether session initialization is already in progress
  const initializingRef = useRef(false);
  const activeRecoveryTaskRef = useRef<string | null>(null);
  // Latest-ref for the recovery-terminal release so pollTaskUntilTerminal's
  // useCallback identity stays stable across host re-renders.
  const onRecoveredTaskTerminalRef = useRef(opts?.onRecoveredTaskTerminal);
  useEffect(() => {
    onRecoveredTaskTerminalRef.current = opts?.onRecoveredTaskTerminal;
  });

  // ──────────────────────────────────────────────────────────────────────
  // Per-turn ledger (concurrent-turn state stomp fix, 2026-08-16).
  // The ref is authoritative and mutated SYNCHRONOUSLY (accessors used by
  // event handlers/effects must never read a lagging state snapshot — the
  // exact class of bug the stale active-thread-id bridge was, PR #855);
  // the version state exists only to re-render/derive.
  // ──────────────────────────────────────────────────────────────────────
  const liveTurnsRef = useRef<Map<string, Prest0nLiveTurn>>(new Map());
  const [liveTurnsVersion, setLiveTurnsVersion] = useState(0);
  const bumpLiveTurns = useCallback(() => setLiveTurnsVersion((v) => v + 1), []);

  /** Prune the bounded terminal ledger (oldest-ended entries first). */
  const pruneTerminalTurns = useCallback(() => {
    const terminals: Prest0nLiveTurn[] = [];
    liveTurnsRef.current.forEach((t) => {
      if (t.phase === 'terminal') terminals.push(t);
    });
    if (terminals.length <= MAX_TERMINAL_TURN_LEDGER) return;
    terminals.sort((a, b) => (a.endedAtMs || 0) - (b.endedAtMs || 0));
    for (const stale of terminals.slice(0, terminals.length - MAX_TERMINAL_TURN_LEDGER)) {
      liveTurnsRef.current.delete(stale.turnKey);
    }
  }, []);

  /** Register a turn at TURN START. A new send only ever writes ITS OWN
   *  entry — it must NEVER cancel/replace another thread's in-flight turn;
   *  the only eviction is the SAME thread's previous terminal entry (the
   *  old "next send clears the last terminal activeTask" semantics). */
  const beginTurn = useCallback((turn: Prest0nLiveTurn) => {
    if (turn.originThreadId) {
      liveTurnsRef.current.forEach((t, key) => {
        if (key !== turn.turnKey && t.phase === 'terminal' && t.originThreadId === turn.originThreadId) {
          liveTurnsRef.current.delete(key);
        }
      });
    }
    liveTurnsRef.current.set(turn.turnKey, turn);
    bumpLiveTurns();
  }, [bumpLiveTurns]);

  /** Patch one turn's entry (poll tick, onTaskQueued binding). No-op when
   *  the turn is no longer tracked (detached/aborted). */
  const patchTurn = useCallback((turnKey: string, patch: Partial<Prest0nLiveTurn>) => {
    const current = liveTurnsRef.current.get(turnKey);
    if (!current) return;
    liveTurnsRef.current.set(turnKey, { ...current, ...patch });
    bumpLiveTurns();
  }, [bumpLiveTurns]);

  /**
   * End a turn.
   *  - 'terminal': keep a bounded ledger entry so the per-task resume
   *    skip-guard survives until the finalized message lands (the old code
   *    kept the last terminal record in activeTask for the same reason).
   *  - 'detach': drop the entry entirely — ownership is deliberately handed
   *    to the resume machinery (poll exhaustion rank #5 / failed send).
   */
  const endTurn = useCallback((
    turnKey: string,
    mode: 'terminal' | 'detach',
    finalTask?: Prest0nTaskRecord | null,
  ) => {
    const current = liveTurnsRef.current.get(turnKey);
    if (!current) return;
    if (mode === 'detach') {
      liveTurnsRef.current.delete(turnKey);
    } else {
      liveTurnsRef.current.set(turnKey, {
        ...current,
        phase: 'terminal',
        endedAtMs: Date.now(),
        task: finalTask !== undefined ? finalTask : current.task,
      });
      pruneTerminalTurns();
    }
    bumpLiveTurns();
  }, [bumpLiveTurns, pruneTerminalTurns]);

  const getTurnForThread = useCallback((threadId: string | null | undefined): Prest0nLiveTurn | null => {
    const target = typeof threadId === 'string' ? threadId.trim() : '';
    if (!target) return null;
    let live: Prest0nLiveTurn | null = null;
    let terminal: Prest0nLiveTurn | null = null;
    liveTurnsRef.current.forEach((t) => {
      if (t.originThreadId !== target) return;
      if (t.phase !== 'terminal') {
        if (!live || t.startedAtMs > live.startedAtMs) live = t;
      } else if (!terminal || (t.endedAtMs || 0) > (terminal.endedAtMs || 0)) {
        terminal = t;
      }
    });
    return live || terminal;
  }, []);

  /** Live (non-terminal) turn count for a thread — the CAPACITY input for
   *  the in-thread parallelism gate (slice 1, 2026-08-19). Dispatch
   *  accounting stays in the reducer maps; THIS ledger is liveness truth. */
  const countLiveTurnsForThread = useCallback((threadId: string | null | undefined): number => {
    const target = typeof threadId === 'string' ? threadId.trim() : '';
    if (!target) return 0;
    let count = 0;
    liveTurnsRef.current.forEach((t) => {
      if (t.originThreadId === target && t.phase !== 'terminal') count += 1;
    });
    return count;
  }, []);

  const getTurnForTask = useCallback((taskId: string | null | undefined): Prest0nLiveTurn | null => {
    const target = typeof taskId === 'string' ? taskId.trim() : '';
    if (!target) return null;
    let live: Prest0nLiveTurn | null = null;
    let terminal: Prest0nLiveTurn | null = null;
    liveTurnsRef.current.forEach((t) => {
      const matches = t.taskId === target || t.turnKey === target || t.task?.taskId === target;
      if (!matches) return;
      if (t.phase !== 'terminal') {
        if (!live || t.startedAtMs > live.startedAtMs) live = t;
      } else if (!terminal || (t.endedAtMs || 0) > (terminal.endedAtMs || 0)) {
        terminal = t;
      }
    });
    return live || terminal;
  }, []);

  const normalizeThreadId = useCallback(
    (rawThreadId?: string | null): string => {
      const explicit = typeof rawThreadId === 'string' ? rawThreadId.trim() : '';
      if (explicit) return explicit;
      const fromConversation = typeof conversationId === 'string' ? conversationId.trim() : '';
      if (fromConversation) return fromConversation;
      // Session-hardening S3 (Miles 2026-07-12): the old literal `'default'`
      // fallback funnelled every non-thread-switcher conversation on every
      // page into ONE shared server session whose recent transcript replayed
      // across them — genuine cross-conversation dialog bleed. Each account
      // now gets a minted, persisted standalone conversation id, rotated on
      // "clear conversation".
      return getOrMintStandaloneConversationId(accountId || 'anon');
    },
    [conversationId, accountId],
  );

  const getThreadStorageKey = useCallback(
    (threadId: string): string => `prest0_session_${accountId}_${threadId}`,
    [accountId],
  );

  const resolveThreadSession = useCallback(
    async (threadId: string, ctx?: OrchestratorPageContext): Promise<SessionInfo> => {
      if (!accountId) {
        throw new Error('Cannot resolve session without accountId');
      }
      const normalizedThreadId = normalizeThreadId(threadId);
      // Session-hardening S3: sessions are SYNTHESIZED deterministically
      // (sess_thr_<threadId>) — no cache to go stale, no pre-flight network
      // round-trips, no per-device divergence. The server resolves/creates
      // the doc from threadId on every turn.
      return resumeOrCreatePrest0nSession(
        accountId,
        normalizedThreadId,
        ctx || pageContext,
      );
    },
    [accountId, normalizeThreadId, pageContext],
  );

  const persistCompletedTaskMessage = useCallback(async (
    task: Prest0nTaskRecord,
    context?: Pick<PersistedQueuedTask, 'threadId' | 'prompt'>,
  ) => {
    const finalResponse = task.result?.finalResponse;
    const threadId = context?.threadId;
    if (!threadId || !finalResponse) return;

    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    // 2026-08-14 duplicate-reply fix (single-writer): when THIS tab's live
    // lane owns the turn, the live finalize already delivered (or will
    // deliver) the answer in place — the server read below can be 150ms+
    // stale (debounced mirror), so it cannot be trusted to prove absence,
    // and this writer's append is exactly what rendered the same reply twice
    // around a connect card. The live path never needs this recovery writer.
    if (isLiveSendInFlight(task.taskId)) return;

    const existing = (await getThreadMessages(uid, threadId).catch(() => null)) || [];
    // RETRY-STABLE anchor (2026-08-27, the duplicated "I hit an error… (ref:
    // pn-mtaupwuc-ova20)" card): this writer keyed on the raw RETRY-HOP
    // taskId, so after following a rescue pointer its copy landed beside the
    // server's under a different id, its existence check missed the server
    // card, and both persisted forever with identical text and the same ref.
    // See resolveClientTaskAnchorId for the derivation contract.
    const anchorTaskId = resolveClientTaskAnchorId(
      task as unknown as { taskId: string; dispatchMetadata?: { threadAnchorTaskId?: unknown } },
    );
    const recoveredMessageId = `prest0n_task_${anchorTaskId}`;
    if (existing.some((m) =>
      m.id === recoveredMessageId
      || (typeof m.taskAnchorId === 'string' && m.taskAnchorId.trim() === recoveredMessageId)
    )) return;

    // 2026-09-08 (smooth playback): the ONE attachment mapper — a recovered video keeps its
    // storagePath (re-mintable after the signed URL expires) and never a base64 twin.
    const builtAttachments = (task.result?.attachmentsSerialized || [])
      .map((att) => toSerializedAttachment(att as RawAttachmentRecord))
      .filter((att): att is SerializedAttachment => att !== null);

    // Carry the turn's usage into the recovered message — refreshed/recovered
    // billable turns previously lost the ⓘ popup entirely (showing "no
    // extensive compute was necessary" for turns the wallet debited).
    const rawUsage = task.result?.usage;
    const recoveredUsage = rawUsage
      ? {
          inputTokens: rawUsage.inputTokens,
          outputTokens: rawUsage.outputTokens,
          model: rawUsage.model,
          provider: rawUsage.provider as any,
          upstreamCostUsd: rawUsage.upstreamCostUsd ?? undefined,
          reasoningTokens: rawUsage.reasoningTokens,
          timestamp: new Date(),
        }
      : undefined;
    // Task docs persist the multi-model breakdown as usage.allGenerations,
    // never as result.tokenUsages (that shape exists only on the live SSE
    // done event) — extract through the shared helper so recovery matches.
    const recoveredUsages = extractTaskTokenUsages(task.result);

    const message: ChatMessage = {
      id: recoveredMessageId,
      isUser: false,
      text: finalResponse,
      html: finalResponse,
      // 2026-08-14 duplicate-reply fix (ordering): stamp at task MINT time,
      // mirroring the server durable writer. A write-time stamp sorted any
      // surviving duplicate BELOW later messages (e.g. a connect card),
      // where the adjacency dedupe could never see it; mint-time keeps every
      // copy of one turn adjacent to its siblings.
      timestamp: new Date(taskMintTimestampMs(task.taskId) || Date.now()),
      sourceTag: 'prest0n_session_response',
      status: 'complete',
      interrupted: false,
      // Convergence anchor (Finding 2) — equals both the id and the server's
      // per-doc key, so every writer of this answer lands on one document.
      taskAnchorId: recoveredMessageId,
      ...(recoveredUsage ? { tokenUsage: recoveredUsage } : {}),
      ...(recoveredUsages && recoveredUsages.length > 0
        ? {
            tokenUsages: recoveredUsages.map((u) => ({
              inputTokens: u.inputTokens,
              outputTokens: u.outputTokens,
              model: u.model,
              provider: u.provider as any,
              upstreamCostUsd: u.upstreamCostUsd ?? undefined,
              reasoningTokens: u.reasoningTokens,
              timestamp: new Date(),
            })),
          }
        : {}),
      ...(builtAttachments.length > 0 ? { attachmentsSerialized: builtAttachments } : {}),
    };

    // Replace a trailing interrupted assistant card with the recovered answer
    // (rather than appending below it), so a refreshed document turn does not
    // leave a stale "Interrupted" card sitting on top of the real answer.
    const nextMessages = mergeRecoveredAnswerIntoMessages(existing, message, {
      taskId: task.taskId,
      turnPrompt: context?.prompt,
    });

    await upsertThreadMessages(uid, 'prest0n_task_recovery', threadId, nextMessages);
  }, []);

  const pollTaskUntilTerminal = useCallback(async (params: {
    taskId: string;
    /** Per-turn ledger key — defaults to taskId (recovery polls). */
    turnKey?: string;
    /** Origin thread captured at TURN START; recovery polls fall back to the
     *  queued record's threadId. */
    originThreadId?: string | null;
    callbacks: TurnStreamCallbacks;
    signal?: AbortSignal;
    session: SessionInfo;
    persistedContext?: PersistedQueuedTask;
    persistCompletionToThread?: boolean;
  }) => {
    const task: Prest0nTaskRecord = {
      taskId: params.taskId,
      accountId: params.session.accountId,
      sessionId: params.session.sessionId,
      status: 'pending',
      executionMode: 'shared-worker',
    };

    // Concurrent-turn stomp fix (2026-08-16): every poll owns exactly ONE
    // ledger entry — never the shared hook state, which a second thread's
    // concurrent poll used to overwrite tick-by-tick. Upsert covers direct
    // callers (the hydrate-recovery poll) that did not beginTurn themselves.
    const turnKey = params.turnKey || params.taskId;
    if (liveTurnsRef.current.has(turnKey)) {
      patchTurn(turnKey, { phase: 'polling', task });
    } else {
      beginTurn({
        turnKey,
        taskId: params.taskId,
        originThreadId: params.originThreadId ?? params.persistedContext?.threadId ?? null,
        sessionId: params.session.sessionId,
        phase: 'polling',
        task,
        startedAtMs: Date.now(),
        isRecovery: !!params.persistCompletionToThread,
      });
    }

    let current = task;
    // §12 PR-F2 (Miles, 2026-09-21: "each task should be more detailed in what it
    // does"): the key is the whole STEP, not the stage id. Keying on the id alone
    // meant a turn's SECOND `post_progress_update` — same id, new words — never
    // reached the pill, so only the first progress line of any turn was ever seen;
    // the same for two tool steps inside one planner iteration.
    let lastStageEmitted: string | null = null;
    // EMPTY-DELIVERY RETRY HANDOFF (2026-08-05, owner repro "3D warehouse
    // apartment" on the Fernando VM lane): when a dedicated-VM turn completes
    // without a deliverable surface, the server enqueues ONE silent salvage
    // retry and quietly terminalizes the ORIGINAL doc (the one this poll
    // watches) as failed/TASK-EMPTY-DELIVERY with
    // dispatchMetadata.emptyDeliveryRetriedBy pointing at the retry. The old
    // behavior rendered the Interrupted card the instant that write landed —
    // WHILE the salvage was still running. Follow the retry instead
    // (single-shot; a retry doc never carries the marker, so no loop) and let
    // ITS terminal state decide what the user sees.
    let pollTaskId = params.taskId;
    let rescueFollowCount = 0;
    const MAX_RESCUE_FOLLOWS = 3;
    // 540 attempts × 2s ≈ 18 min of live polling. A strict-mode dedicated-VM
    // long turn can now span multiple 540s GCF resume hops (see taskQueue.ts
    // MAX_VM_RESUME_HOPS), so the live poller must outlast the resume chain.
    // A hard reload still recovers via serverTaskResume + the durable thread
    // write, so this only governs the in-session live wait.
    // Durability audit rank #5 (2026-07-13): 540 × 2s ≈ 18 min silently
    // under-covered long deep-research turns. 1050 × 2s ≈ 35 min now covers
    // the server sweeper's worst-case terminalization window (~30 min), so a
    // genuinely dead task resolves to a durable 'failed' status INSIDE this
    // loop instead of exhausting it.
    const POLL_MAX_ATTEMPTS = 1050;

    // Stall watchdog (2026-07-31): fingerprint of the last observed updatedAt
    // (serialized — the HTTP transport ships Firestore Timestamps as
    // {_seconds,_nanoseconds} maps, so we compare shape-agnostically), the
    // wall-clock time we first saw it, and one-shot flags for the holding
    // label and the stall abort.
    let stallBaselineFingerprint: string | null = null;
    let stallBaselineAtMs = 0;
    let stallHoldingLabelShown = false;
    let stallEscalatedLabelShown = false;
    // Smooth streaming (2026-08-23): the partial answer text already fed to
    // onTextChunk on this turn, and a latch for the case where the server's
    // partial stops extending what we painted (a rewrite, a continuation hop
    // restarting the synthesis).
    let emittedPartial = '';
    let partialDiverged = false;

    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
      if (params.signal?.aborted) break;
      await new Promise((resolve) => setTimeout(resolve, 2000));
      // Stop fix (2026-07-31): re-check the abort signal after EVERY await —
      // a user Stop that lands during the sleep or the status fetch must fire
      // NO further callbacks (a late onDone painted the stopped turn's answer
      // "out of nowhere" AND its stopProcessing released the NEXT turn's
      // processing flag mid-flight).
      if (params.signal?.aborted) break;
      const latest = await getPrest0nTaskStatus(pollTaskId);
      if (params.signal?.aborted) break;
      if (!latest) continue;

      current = latest;
      // Tick update lands on THIS turn's ledger entry only — a concurrent
      // thread's poll can no longer see its activeTask flip to ours.
      patchTurn(turnKey, { task: latest });
      params.callbacks.onTaskStatus?.(latest);

      // SMOOTH STREAMING (owner directive 2026-08-23) — the async/202 lane's
      // incremental channel. The server writes a throttled answer-so-far onto
      // the task doc we already poll; feed only what has NOT been painted yet
      // (onTextChunk APPENDS) so the bubble grows instead of duplicating. Any
      // divergence from what we painted stops partial feeding for this turn —
      // the terminal finalTextOverride below replaces the whole bubble with
      // the authoritative answer regardless, so this can never leave the user
      // looking at wrong text.
      const partial = typeof latest.partialAnswer === 'string' ? latest.partialAnswer : '';
      if (partial && !partialDiverged && partial.length > emittedPartial.length) {
        if (partial.startsWith(emittedPartial)) {
          const suffix = partial.slice(emittedPartial.length);
          emittedPartial = partial;
          try {
            params.callbacks.onTextChunk?.(suffix);
          } catch (err) {
            console.warn('[usePrest0nSession] onTextChunk callback threw on partial answer', err);
          }
        } else {
          partialDiverged = true;
        }
      }

      const currentStage = latest.currentStage;
      const stageKey = currentStage && currentStage.stage
        ? [currentStage.stage, currentStage.stepNumber ?? '', (currentStage.label || '').trim()].join('\u0000')
        : null;
      if (stageKey && stageKey !== lastStageEmitted) {
        lastStageEmitted = stageKey;
        const label = (currentStage?.label || '').trim();
        if (label) {
          try {
            params.callbacks.onThinking?.(label);
          } catch (err) {
            console.warn('[usePrest0nSession] onThinking callback threw on stage update', err);
          }
        }
      }

      if (latest.status === 'completed') {
        const handoff = (latest.result as any)?.workflowHandoff;
        if (handoff && typeof handoff === 'object' && handoff.type) {
          const handled = params.callbacks.onWorkflowHandoff?.(handoff);
          if (handled === true) {
            params.callbacks.onDone?.(true, undefined);
            clearQueuedTask(params.session.accountId, params.session.sessionId);
            break;
          }
        }

        const finalResponse = latest.result?.finalResponse;
        // REVIEW-20260824 (critical): a surface-owning turn (full-pipeline deep
        // research) deliberately ships finalResponse EMPTY and carries the real
        // report only in surfaceOwnedText — but once partials painted a bubble,
        // streamingMessageId is non-null, the orphan-deliverable path refuses
        // the turn, and the bubble finalized with the truncated 24k checkpoint.
        // The authoritative body for a partial-painted turn is therefore
        // finalResponse OR (when the tool owns the surface) surfaceOwnedText.
        // Gated on emittedPartial so turns that never streamed keep the exact
        // pre-streaming behavior (the orphan path still owns those).
        const surfaceOwnedBodyForStream =
          emittedPartial
            && latest.result?.surfaceOwnedByTool
            && typeof latest.result?.surfaceOwnedText === 'string'
            ? latest.result.surfaceOwnedText
            : '';
        const authoritativeBody = finalResponse || surfaceOwnedBodyForStream;
        if (authoritativeBody) {
          // Smooth streaming (2026-08-23): when partials already painted a
          // prefix, hand over only the remaining tail so the answer FINISHES
          // arriving instead of jumping — and never append a second full copy
          // of what is already on screen. `finalTextOverride` below then
          // replaces the bubble with the authoritative text either way, which
          // is what makes a diverged partial self-correcting.
          const suffix = emittedPartial && authoritativeBody.startsWith(emittedPartial)
            ? authoritativeBody.slice(emittedPartial.length)
            : (emittedPartial ? '' : authoritativeBody);
          if (suffix) params.callbacks.onTextChunk?.(suffix);
        }

        const rawUsage = latest.result?.usage;
        const mappedUsage = rawUsage
          ? {
              inputTokens: rawUsage.inputTokens,
              outputTokens: rawUsage.outputTokens,
              model: rawUsage.model,
              provider: rawUsage.provider as any,
              upstreamCostUsd: rawUsage.upstreamCostUsd ?? undefined,
              reasoningTokens: rawUsage.reasoningTokens,
            }
          : undefined;
        const taskAttachments = latest.result?.attachmentsSerialized;
        const taskSurfaceOwnedByTool = latest.result?.surfaceOwnedByTool;
        const taskSurfaceOwnedText = latest.result?.surfaceOwnedText;

        // ORG-USAGE-2026: async-lane parity with the SSE done event — publish
        // the pre-block limit warning to the banner channel.
        publishUsageWarning((latest.result as any)?.usageWarning);

        params.callbacks.onDone?.(
          true,
          mappedUsage,
          taskAttachments,
          taskSurfaceOwnedByTool,
          taskSurfaceOwnedText,
          extractTaskTokenUsages(latest.result),
          // Smooth streaming (2026-08-23): once partials painted anything, the
          // finalize must REPLACE rather than trust the accumulation — same
          // mechanism R11 uses for glued VM fragments. Only when partials ran,
          // so turns that never streamed keep their exact previous behavior.
          // REVIEW-20260824: authoritativeBody (not finalResponse) so a
          // surface-owning turn's bubble finalizes with the FULL report, not
          // the truncated checkpoint the partials painted.
          emittedPartial && authoritativeBody ? authoritativeBody : undefined,
        );

        if (params.persistCompletionToThread) {
          try {
            await persistCompletedTaskMessage(latest, params.persistedContext);
          } finally {
            // H1 (2026-07-14): the recovery poll runs with EMPTY callbacks —
            // no onDone exists to release the originating thread's processing
            // flag, so the chip/row spinner outlived the turn (durable write
            // above rendered the report; the flag leaked until the 10-min
            // floor). Release AFTER the durable write so the finalized
            // content lands before the in-flight indication clears (finalize
            // ordering rule) — but in a `finally` so a persist failure can
            // never strand the flag either (the task IS terminal regardless).
            try {
              onRecoveredTaskTerminalRef.current?.(
                params.persistedContext?.threadId || null,
                params.taskId,
              );
            } catch {
              // The release must never break the poll's completion flow.
            }
          }
        }
        clearQueuedTask(params.session.accountId, params.session.sessionId);
        break;
      }

      if (latest.status === 'failed') {
        // Stop race (2026-07-31): a task the USER cancelled server-side
        // (prest0nCancelTask) terminalizes as status:'failed' with
        // errorCode:'cancelled_by_user'. Normally the local abort kills this
        // poll before it ever observes that write — but when the poll DOES
        // see it first (or the abort raced late), it must be a SILENT
        // terminal: the user's own Stop already rendered the stopped chip,
        // so no failure/retry bubble may render on top of it.
        if (latest.errorCode === 'cancelled_by_user') {
          // F1 (2026-08-03): release UNCONDITIONALLY. This branch was gated
          // on persistCompletionToThread, so a cancel that arrived from
          // ANOTHER tab/device (or an archive-driven server cancel) left
          // this tab's pending widget spinning forever — the silent
          // terminal still has to clear the thread's processing state.
          try {
            onRecoveredTaskTerminalRef.current?.(
              params.persistedContext?.threadId || null,
              params.taskId,
            );
          } catch {
            // The release must never break the poll's completion flow.
          }
          clearQueuedTask(params.session.accountId, params.session.sessionId);
          break;
        }
        // RESCUE-RETRY HANDOFF (2026-08-05 empty-delivery; generalized
        // 2026-08-11 for vm-transport and the watchdog deploy-orphan retry —
        // the Fernando DR "stopped waiting" incident): a terminalized doc
        // whose dispatchMetadata points at a salvage retry is not terminal
        // for the USER. Re-point the poll at the retry and keep the working
        // surface. Bounded follow count — each rescue kind is one-shot
        // server-side, but they can chain (e.g. empty-delivery retry later
        // deploy-orphaned and watchdog-retried).
        const rescueMeta = (latest as unknown as {
          dispatchMetadata?: {
            emptyDeliveryRetriedBy?: unknown;
            vmTransportRetriedBy?: unknown;
            watchdogRetriedBy?: unknown;
            docContinuedBy?: unknown;
            intakeContinuedBy?: unknown;
          };
        })?.dispatchMetadata;
        // DOC-COMPLETENESS (2026-09-01/02): a document cut at the output
        // ceiling hands off to a doc-continue hop the same way — the original
        // terminalizes quietly with `docContinuedBy`. The refresh-resume path
        // (serverTaskResume.ts) followed it from day one; this LIVE poll did
        // not, so a live document turn ended in onError (an Interrupted card)
        // while the hop delivered the real answer minutes later — two
        // finalized copies for one turn. Same list, both followers.
        const rescuePointer = [
          rescueMeta?.emptyDeliveryRetriedBy,
          rescueMeta?.vmTransportRetriedBy,
          rescueMeta?.watchdogRetriedBy,
          rescueMeta?.docContinuedBy,
          // INTAKE-COMPLETENESS (2026-09-11): a packet whose READING ran out of
          // envelope hands off the same way — the original terminalizes quietly
          // and the hop finishes the files. Its pointer was written under a name
          // no client had ever read, so a live attachment turn ended on the
          // empty-delivery card ("Tap Regenerate… reported to the on-call team")
          // while the hop delivered the real answer. Exactly the shape the
          // doc-continue note above describes; same list, same fix.
          rescueMeta?.intakeContinuedBy,
          // RE-PLAN (2026-09-15, PLAN-mid-turn-fold-in §4.2): the primary's
          // note turn stopped this task and started the merged ask as a new
          // one — the original terminalizes quietly with `replacedByTaskId`
          // and the reply arrives on the replacement. Same list, same follow.
          (latest as { replacedByTaskId?: unknown }).replacedByTaskId,
        ].find((v): v is string => typeof v === 'string' && !!v.trim() && v.trim() !== pollTaskId);
        if (rescuePointer && rescueFollowCount < MAX_RESCUE_FOLLOWS) {
          rescueFollowCount += 1;
          pollTaskId = rescuePointer.trim();
          stallBaselineFingerprint = null; // fresh doc → fresh stall baseline
          continue;
        }
        // 2026-06 reliability overhaul: prefer the structured errorInfo the
        // backend now stamps on failed task docs (human-readable message +
        // traceId) over the legacy raw error string, which can be a raw
        // JSON/stack dump (the FERN-ROUTE-005 class) that must never reach
        // the chat surface. The raw string stays as the message fallback for
        // old task docs; consumers receiving errorInfo render it instead.
        const errorInfo = latest.errorInfo;
        params.callbacks.onError?.(
          errorInfo?.userMessage || latest.error || latest.result?.error || 'Background task failed',
          false,
          errorInfo,
        );
        // H1 (2026-07-14): terminal is terminal — a recovered task that FAILED
        // must release the originating thread's processing flag too (the empty
        // recovery callbacks mean the onError above was a no-op on this path).
        if (params.persistCompletionToThread) {
          try {
            onRecoveredTaskTerminalRef.current?.(
              params.persistedContext?.threadId || null,
              params.taskId,
            );
          } catch {
            // The release must never break the poll's completion flow.
          }
        }
        clearQueuedTask(params.session.accountId, params.session.sessionId);
        break;
      }

      // ────────────────────────────────────────────────────────────────────
      // Stall watchdog (2026-07-31) — only reached while the task is still
      // pending/running. An acked-but-never-progressing task keeps updatedAt
      // frozen; the sweeper takes 12-25+ min to terminalize it, which used to
      // mean a silent hang. Docs without an updatedAt field (should not
      // happen — the queue stamps it at create) are indeterminate, so the
      // watchdog stands down rather than risk failing a healthy task.
      // ────────────────────────────────────────────────────────────────────
      const rawUpdatedAt = (latest as unknown as Record<string, unknown>).updatedAt;
      if (rawUpdatedAt == null) {
        stallBaselineFingerprint = null;
        continue;
      }
      let updatedAtFingerprint: string;
      try {
        updatedAtFingerprint = JSON.stringify(rawUpdatedAt);
      } catch {
        updatedAtFingerprint = String(rawUpdatedAt);
      }
      if (updatedAtFingerprint !== stallBaselineFingerprint) {
        // Fresh progress (stage keep-alive, VM resume hop, status flip) —
        // reset the timer and re-arm the holding label for a later re-stall.
        stallBaselineFingerprint = updatedAtFingerprint;
        stallBaselineAtMs = Date.now();
        stallHoldingLabelShown = false;
        stallEscalatedLabelShown = false;
        continue;
      }
      const stalledForMs = Date.now() - stallBaselineAtMs;
      if (stalledForMs >= TASK_STALL_ERROR_MS && !stallEscalatedLabelShown) {
        // 2026-08-11 (Fernando DR "stopped waiting" incident): this used to
        // ABORT the poll and render the Interrupted/Regenerate card — while
        // the server was still working (dr-continue hops froze updatedAt for
        // 8-18 min by design) or a watchdog salvage was about to run. The
        // user regenerated on top of a live run and double-spent a full
        // deep-research pipeline. The server sweeper GUARANTEES a terminal
        // verdict on a genuinely dead task within ~22 min, the failed branch
        // above now follows watchdog rescues, and POLL_MAX_ATTEMPTS (~35 min)
        // backstops a broken sweeper — so the honest client behavior here is
        // to keep waiting and say so, not to declare death first.
        stallEscalatedLabelShown = true;
        console.warn(
          `[usePrest0nSession] Task ${params.taskId} made no progress for ${Math.round(stalledForMs / 1000)}s — continuing to poll (server sweeper owns the terminal verdict)`,
        );
        try {
          params.callbacks.onThinking?.(TASK_STALL_ESCALATED_HOLDING_LABEL);
        } catch {
          // never let a UI callback break the poll
        }
      }
      if (stalledForMs >= TASK_STALL_HOLDING_LABEL_MS && !stallHoldingLabelShown) {
        stallHoldingLabelShown = true;
        try {
          params.callbacks.onThinking?.(TASK_STILL_WORKING_HOLDING_LABEL);
        } catch {
          // never let a UI callback break the poll
        }
      }
    }

    // Durability audit rank #5 (2026-07-13): if the loop exhausted with the
    // task still non-terminal, DETACH cleanly — the old code kept activeTask
    // pointing at the task, which made shouldSkipLiveTaskResume() treat it as
    // live forever and permanently blocked every later re-attach in this tab
    // (the in-session infinite "Step N of 3" pill). Clearing activeTask hands
    // ownership to the resume machinery (widget keeps its serverTaskId), and
    // the honest holding label replaces a stale stage echo.
    const exhaustedNonTerminal =
      current && (current.status === 'pending' || current.status === 'running') && !params.signal?.aborted;
    if (exhaustedNonTerminal) {
      // ~35 min non-terminal means even the server sweeper (guaranteed
      // verdict ≤ ~22 min) failed to rule — NOW the stall card is honest.
      // This is the only place the client declares a stall (2026-08-11: the
      // former 6-min abort was demoted to a holding label).
      try {
        params.callbacks.onError?.(
          `Task ${params.taskId} still non-terminal after poll exhaustion`,
          false,
          buildTaskStallErrorInfo(params.taskId),
        );
      } catch (err) {
        console.warn('[usePrest0nSession] poll-exhaustion onError callback threw', err);
      }
      if (params.persistCompletionToThread) {
        try {
          onRecoveredTaskTerminalRef.current?.(
            params.persistedContext?.threadId || null,
            params.taskId,
          );
        } catch {
          // The release must never break the poll's stand-down flow.
        }
      }
      endTurn(turnKey, 'detach');
      return;
    }
    // Terminal (or user-aborted) — keep a bounded terminal ledger entry so
    // the per-task resume skip-guard holds until the finalized/stopped
    // message has landed (the old code kept the last terminal record in the
    // shared activeTask for exactly this reason — but only for ONE turn).
    endTurn(turnKey, 'terminal', current);
  }, [persistCompletedTaskMessage, beginTurn, patchTurn, endTurn]);

  // ========================================================================
  // Session Initialization
  // ========================================================================

  const initSession = useCallback(async () => {
    if (!accountId || initializingRef.current) return;
    initializingRef.current = true;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const bootstrapThreadId = normalizeThreadId(conversationId);
      const session = await resolveThreadSession(bootstrapThreadId, pageContext);
      sessionRef.current = session;
      setState((prev) => ({ ...prev, session, isLoading: false }));

      // 2026-08-14 duplicate-reply fix: the hydrate probe must distinguish a
      // SERVED not-found (task never existed / already swept → clear the
      // pre-flight record, arm no recovery) from network trouble (throw →
      // keep the record for the next hydrate). getPrest0nTaskStatus flattens
      // both to null, so use the detailed variant here.
      const probeTaskStatus = async (taskId: string): Promise<unknown | null> => {
        const result = await getPrest0nTaskStatusDetailed(taskId);
        if (result.kind === 'ok') return result.task;
        if (result.kind === 'network_error') {
          throw new Error(String((result.error as any)?.message ?? result.error ?? 'task status probe failed (network)'));
        }
        return null;
      };
      const persisted = await resumeQueuedTaskOnHydrate({
        accountId,
        sessionId: session.sessionId,
        getTaskStatus: probeTaskStatus,
      })
        // Migration fallback (S3): a task queued under the retired shared
        // bootstrap session (`sess_thr_default`) just before this deploy must
        // still be recoverable once, on the first post-deploy hydrate.
        || await resumeQueuedTaskOnHydrate({
          accountId,
          sessionId: 'sess_thr_default',
          getTaskStatus: probeTaskStatus,
        });
      if (persisted && activeRecoveryTaskRef.current !== persisted.taskId) {
        activeRecoveryTaskRef.current = persisted.taskId;
        void pollTaskUntilTerminal({
          taskId: persisted.taskId,
          callbacks: {},
          session,
          persistedContext: persisted,
          persistCompletionToThread: true,
        }).finally(() => {
          if (activeRecoveryTaskRef.current === persisted.taskId) {
            activeRecoveryTaskRef.current = null;
          }
        });
      }

      fernandoTrace(
        'SESSION_INIT_SUCCESS',
        { accountId, sessionId: session.sessionId },
        isFernandoStrictMode(accountId)
      );
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error('[usePrest0nSession] Failed to initialize session:', error);
      fernandoTrace(
        'SESSION_INIT_FAIL',
        { accountId, error: error.message, code: FERN_FE_CODES.SESSION_INIT_FAILED },
        isFernandoStrictMode(accountId)
      );
      setState((prev) => ({ ...prev, error, isLoading: false }));
    } finally {
      initializingRef.current = false;
    }
  }, [accountId, conversationId, normalizeThreadId, resolveThreadSession]); // pageContext excluded — handled by context update effect

  // Initialize on mount (or when accountId changes)
  useEffect(() => {
    if (accountId) {
      initSession();
    } else {
      // Clear session state if account ID is removed
      sessionRef.current = null;
      liveTurnsRef.current.clear();
      bumpLiveTurns();
      setState({ session: null, isStreaming: false, isLoading: false, isPollingTask: false, activeTask: null, error: null });
    }
  }, [accountId, initSession, bumpLiveTurns]);

  // ========================================================================
  // Page Context Updates
  // ========================================================================

  // When pageContext changes, update the session on the backend (best-effort)
  const prevPageContextRef = useRef<OrchestratorPageContext | undefined>(undefined);

  useEffect(() => {
    const session = sessionRef.current;
    if (!session || !pageContext) return;

    // Only update if pathname actually changed
    if (prevPageContextRef.current?.pathname === pageContext.pathname) return;

    prevPageContextRef.current = pageContext;
    updatePrest0nSessionContext(session.sessionId, pageContext).catch((err) => {
      console.warn('[usePrest0nSession] Page context update failed (non-fatal):', err);
    });
  }, [pageContext]);

  // ========================================================================
  // Send Turn
  // ========================================================================

  /**
   * The refusal from the most recent failed send. A REF, not state: the retry
   * loop in useGlobalTools reads it synchronously between attempts, and a
   * React state round trip would not have landed yet.
   */
  const lastTurnRefusalRef = useRef<TurnRefusal | null>(null);

  const consumeTurnRefusal = useCallback((): TurnRefusal | null => {
    const refusal = lastTurnRefusalRef.current;
    lastTurnRefusalRef.current = null;
    return refusal;
  }, []);

  const sendTurn = useCallback(
    async (
      message: string,
      callbacks: TurnStreamCallbacks,
      options?: {
        attachments?: SerializedAttachment[];
        pageContext?: OrchestratorPageContext;
        signal?: AbortSignal;
        documentContent?: string;
        responseMode?: 'stream' | 'async' | 'auto';
        threadId?: string;
        ingestJobId?: string;
        requestedWorkflow?: 'deep-research' | 'routines';
        clientTaskId?: string;
        replyTo?: import('../../types/messageReply').MessageReplyRef | null;
      }
    ): Promise<boolean> => {
      // Each attempt starts clean: a refusal describes THIS send or nothing.
      lastTurnRefusalRef.current = null;
      const threadId = normalizeThreadId(options?.threadId);
      const session = await resolveThreadSession(threadId, options?.pageContext || pageContext);
      sessionRef.current = session;
      setState((prev) => ({ ...prev, session, error: null }));

      // CONCURRENT-TURN STATE STOMP FIX (2026-08-16, owner repro post-#855:
      // Drive-connect answer from thread B rendered into thread A's DR
      // transcript). This entry used to force
      // { isStreaming: true, isPollingTask: false, activeTask: null } on the
      // SHARED state — a second thread's send therefore cleared/replaced the
      // state of ANOTHER thread's in-flight poll, and every consumer reading
      // the shared fields at delivery time misbound to "whichever turn wrote
      // last". Each send now registers its OWN ledger entry, keyed for its
      // whole life and bound to its ORIGIN THREAD captured here at TURN
      // START (the PR #855 resume-delivery discipline, applied to live
      // sends). A new send never cancels, replaces, or even touches another
      // thread's in-flight turn.
      const turnKey = (options?.clientTaskId || '').trim() || mintLocalTurnKey();
      beginTurn({
        turnKey,
        taskId: (options?.clientTaskId || '').trim() || null,
        originThreadId: threadId,
        sessionId: session.sessionId,
        phase: 'streaming',
        task: null,
        startedAtMs: Date.now(),
        isRecovery: false,
      });

      // PRE-FLIGHT STAMP (stamp-race fix): when the caller minted the task id,
      // persist the queued-task recovery record BEFORE the network call — a
      // refresh during the request/202 window can then recover from
      // localStorage even though onTaskQueued never fired. If the route turns
      // out inline (no task is ever created), the record is cleared right
      // after the stream completes below.
      let taskWasQueued = false;
      let sendFailed = false;
      if (options?.clientTaskId) {
        persistQueuedTask({
          taskId: options.clientTaskId,
          accountId: session.accountId,
          sessionId: session.sessionId,
          threadId,
          prompt: message,
          queuedAt: Date.now(),
        });
      }

      try {
        const turnAttachments: TurnAttachment[] | undefined = options?.attachments?.length
          ? toTurnAttachments(options.attachments)
          : undefined;

        const mergedCallbacks: TurnStreamCallbacks = {
          ...callbacks,
          onTaskQueued: async (taskId, status) => {
            taskWasQueued = true;
            persistQueuedTask({
              taskId,
              accountId: session.accountId,
              sessionId: session.sessionId,
              threadId,
              prompt: message,
              queuedAt: Date.now(),
            });

            // Bind the SERVER task id to THIS turn's ledger entry BEFORE any
            // await, so no effect can observe a queued-but-unbound turn. The
            // callbacks delivered from here on are the ones closed over at
            // TURN START — deliveries can never re-read "current" state.
            patchTurn(turnKey, { taskId, phase: 'polling' });
            callbacks.onTaskQueued?.(taskId, status);

            await triggerPrest0nTask(taskId).catch((err) => {
              console.warn('[usePrest0nSession] Failed to trigger task immediately:', err);
            });

            await pollTaskUntilTerminal({
              taskId,
              turnKey,
              originThreadId: threadId,
              callbacks,
              signal: options?.signal,
              session,
              persistedContext: loadQueuedTask(session.accountId, session.sessionId) || undefined,
              persistCompletionToThread: false,
            });
          },
        };

        await sendPrest0nTurn(session.sessionId, message, mergedCallbacks, {
          attachments: turnAttachments,
          pageContext: options?.pageContext,
          signal: options?.signal,
          documentContent: options?.documentContent,
          responseMode: options?.responseMode || 'auto',
          threadId,
          ingestJobId: options?.ingestJobId,
          requestedWorkflow: options?.requestedWorkflow,
          clientTaskId: options?.clientTaskId,
          replyTo: options?.replyTo,
        });

        // Inline route completed without ever queueing a task — drop the
        // pre-flight recovery record so a later hydrate doesn't poll a task
        // that never existed.
        if (options?.clientTaskId && !taskWasQueued) {
          clearQueuedTask(session.accountId, session.sessionId);
        }

        return true;
      } catch (err) {
        if (options?.signal?.aborted) {
          // User cancelled — not an error
          return true;
        }
        sendFailed = true;
        // A typed refusal is an ANSWER, not a dead start: record it so the
        // caller can stop retrying and show the server's own sentence.
        lastTurnRefusalRef.current = refusalFromError(err);
        const error = err instanceof Error ? err : new Error(String(err));
        console.error('[usePrest0nSession] Turn failed:', error);
        // THE FAILURE THAT LEFT NO TRACE (QA Series B, B3, 2026-09-02).
        //
        // A turn came back "Prest0n couldn't start this reply. Tap retry" in
        // live production and there was nothing anywhere to diagnose it with.
        // This is why: the throw that caused it is caught HERE, printed to the
        // user's own browser console, parked in React state that nothing
        // reads for diagnosis, and dropped. The caller only learns `false`,
        // retries twice, and renders the card — so the most user-visible
        // failure class in chat is the one failure class with no record.
        //
        // The fix is the one the sibling path already had: the pattern-match
        // failure lane posts to orchestratorDecisionLog, and its comment says
        // exactly why ("this branch used to DISCARD the error — nothing
        // reached the server, so production failures here were
        // undiagnosable", 2026-08-18 RCA). Same lesson, other branch.
        //
        // Fire-and-forget, never throws, keepalive — an observability leaf.
        // Every attempt is logged, not just the last: "failed once then
        // succeeded" and "failed three times" are different faults, and the
        // first is invisible to the user and so to us.
        logOrchestratorTurnError(error, 'prest0n-session-send-start');
        setState((prev) => ({ ...prev, error }));
        return false;
      } finally {
        // Old code flipped the SHARED isStreaming flag here — which a
        // concurrent thread's send also owned. Only THIS turn settles now:
        // an async route already moved its entry to 'polling' (the poll owns
        // the terminal transition); an inline/aborted stream turn goes
        // terminal (the bounded ledger entry keeps the pre-flight
        // clientTaskId resume skip-guard alive until the finalize lands);
        // a failed start detaches entirely so the retry/resume machinery
        // owns the task from here.
        const settled = liveTurnsRef.current.get(turnKey);
        if (settled && settled.phase === 'streaming') {
          endTurn(turnKey, sendFailed ? 'detach' : 'terminal');
        }
      }
    },
    [normalizeThreadId, pageContext, resolveThreadSession, beginTurn, patchTurn, endTurn]
  );

  // ========================================================================
  // Force Refresh
  // ========================================================================

  const refreshSession = useCallback(async () => {
    if (accountId && sessionRef.current?.conversationId) {
      const threadId = normalizeThreadId(sessionRef.current.conversationId);
      // Legacy hygiene: pre-S3 code persisted negotiated ids here. Synthesized
      // sessions no longer read or write this key; keep clearing it so stale
      // entries age out.
      localStorage.removeItem(getThreadStorageKey(threadId));
    }
    sessionRef.current = null;
    setState((prev) => ({ ...prev, session: null }));
    initializingRef.current = false;
    await initSession();
  }, [accountId, getThreadStorageKey, initSession, normalizeThreadId]);

  // ──────────────────────────────────────────────────────────────────────
  // Derived legacy surface (concurrent-turn state stomp fix, 2026-08-16).
  // The shared {isStreaming, isPollingTask, activeTask} fields are now a
  // VIEW over the per-turn ledger: the most recently STARTED live turn wins
  // (mirroring the old last-writer semantics for existing consumers — send
  // gates, turn indicators, SpendChip), falling back to the most recently
  // ENDED terminal entry for activeTask (the old "keep the last terminal
  // record" behavior). Consumers that need thread- or task-correctness use
  // getTurnForThread/getTurnForTask instead of these shared fields.
  // ──────────────────────────────────────────────────────────────────────
  let derivedLive: Prest0nLiveTurn | null = null;
  let derivedTerminal: Prest0nLiveTurn | null = null;
  for (const t of Array.from(liveTurnsRef.current.values())) {
    if (t.phase !== 'terminal') {
      if (!derivedLive || t.startedAtMs > derivedLive.startedAtMs) derivedLive = t;
    } else if (!derivedTerminal || (t.endedAtMs || 0) > (derivedTerminal.endedAtMs || 0)) {
      derivedTerminal = t;
    }
  }
  const derivedPrimary: Prest0nLiveTurn | null = derivedLive || derivedTerminal;

  return {
    ...state,
    isStreaming: !!derivedLive && derivedLive.phase === 'streaming',
    isPollingTask: !!derivedLive && derivedLive.phase === 'polling',
    activeTask: derivedPrimary ? derivedPrimary.task : null,
    sendTurn,
    consumeTurnRefusal,
    refreshSession,
    getTurnForThread,
    getTurnForTask,
    countLiveTurnsForThread,
    liveTurnsVersion,
  };
}
